import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, CartesianGrid } from "recharts";
import { FileText, CheckCircle2, XCircle, Clock, Calendar, AlertCircle, TrendingUp, TrendingDown } from "lucide-react";

const COLORS = ["#083CA6", "#00883A", "#EAB308", "#0284C7", "#DC2626", "#7C3AED"];
const BAR_RADIUS = [4, 4, 0, 0];

function StatCard({ icon: Icon, label, value, color, testid }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-2">
          <div className={`w-10 h-10 rounded-md flex items-center justify-center ${color}`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div className="text-xs text-slate-500 uppercase tracking-wider">{label}</div>
        </div>
        <div className="text-3xl font-black text-slate-900">{value}</div>
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);

  useEffect(() => {
    let active = true;
    Promise.all([api.get("/dashboard/stats"), api.get("/dashboard/charts")])
      .then(([s, c]) => { if (active) { setStats(s.data); setCharts(c.data); } })
      .catch((e) => logger.error("Falha ao carregar dashboard", e));
    return () => { active = false; };
  }, []);

  if (!stats || !charts) return <div className="p-8 text-slate-500">Carregando...</div>;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="text-3xl font-black text-[#083CA6]">Dashboard</h1>
        <p className="text-sm text-slate-600">Visão geral das inscrições MCMV 2026</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={FileText} label="Total" value={stats.total} color="bg-[#083CA6]" testid="stat-total" />
        <StatCard icon={Calendar} label="Hoje" value={stats.hoje} color="bg-[#0284C7]" testid="stat-hoje" />
        <StatCard icon={CheckCircle2} label="Aprovadas" value={stats.aprovadas} color="bg-[#00883A]" testid="stat-aprovadas" />
        <StatCard icon={XCircle} label="Indeferidas" value={stats.indeferidas} color="bg-[#DC2626]" testid="stat-indeferidas" />
        <StatCard icon={Clock} label="Em Análise" value={stats.em_analise} color="bg-[#7C3AED]" testid="stat-em-analise" />
        <StatCard icon={AlertCircle} label="Pendentes" value={stats.pendentes} color="bg-[#EAB308]" testid="stat-pendentes" />
        <StatCard icon={TrendingUp} label="Aptas Faixa 1" value={stats.aptas} color="bg-emerald-600" testid="stat-aptas" />
        <StatCard icon={TrendingDown} label="Inaptas" value={stats.inaptas} color="bg-slate-500" testid="stat-inaptas" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-lg">Inscrições por Bairro</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={charts.por_bairro}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="label" fontSize={11} angle={-20} textAnchor="end" height={70} />
                <YAxis fontSize={11} />
                <Tooltip />
                <Bar dataKey="value" fill="#083CA6" radius={BAR_RADIUS} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-lg">Por Faixa de Renda</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={charts.por_renda} dataKey="value" nameKey="label" outerRadius={90} label>
                  {charts.por_renda.map((entry) => <Cell key={entry.label} fill={COLORS[Math.abs(entry.label.charCodeAt(0)) % COLORS.length]} />)}
                </Pie>
                <Legend />
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-lg">Por Estado Civil</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={charts.por_estado_civil}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="label" fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip />
                <Bar dataKey="value" fill="#00883A" radius={BAR_RADIUS} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-lg">Por Quantidade de Dependentes</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={charts.por_dependentes}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="label" fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip />
                <Bar dataKey="value" fill="#EAB308" radius={BAR_RADIUS} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
